import {
  CompositeAnalyticsAdapter,
  LocalAnalyticsAdapter,
  type AnalyticsAdapter,
} from "./AnalyticsAdapter";
import { BackendCollectorDestination } from "./BackendCollectorDestination";
import { DeferredAnalyticsAdapter } from "./deferred";
import { PostHogDestination } from "./PostHogDestination";
import { SentryDestination } from "./SentryDestination";

export interface AnalyticsEnv {
  VITE_POSTHOG_KEY?: string;
  VITE_POSTHOG_HOST?: string;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_COLLECTOR_URL?: string;
  VITE_PLATFORM?: string;
}

/** Platform values the collector envelope accepts (shared/analyticsEnvelope). */
const COLLECTOR_PLATFORMS: ReadonlySet<string> = new Set([
  "yandex",
  "crazygames",
  "poki",
  "itch",
  "web",
  "unknown",
]);

/**
 * Maps the game's build platform (local | yandex | crazygames | gamepix) onto the
 * collector's allowlist, so events are accepted instead of rejected. Unknown values
 * (local, gamepix) fall back to "web" - they are all browser/HTML5 builds.
 */
function collectorPlatform(platform: string | undefined): string {
  return platform && COLLECTOR_PLATFORMS.has(platform) ? platform : "web";
}

/**
 * Assembles the analytics composite from environment config. LocalAnalyticsAdapter
 * is always active; PostHog joins when both key and host are set, Sentry when a
 * DSN is set. With no env keys only Local runs (no external SDK). SDK-backed
 * destinations are wrapped in {@link DeferredAnalyticsAdapter} so calls made while
 * their SDK is still loading are replayed instead of lost.
 */
export function buildAnalytics(
  env: AnalyticsEnv,
  options?: { externalEnabled?: boolean },
): AnalyticsAdapter {
  const externalEnabled = options?.externalEnabled ?? true;
  const destinations: AnalyticsAdapter[] = [new LocalAnalyticsAdapter()];

  if (externalEnabled && env.VITE_POSTHOG_KEY && env.VITE_POSTHOG_HOST) {
    const posthog = new PostHogDestination({
      key: env.VITE_POSTHOG_KEY,
      host: env.VITE_POSTHOG_HOST,
    });
    destinations.push(new DeferredAnalyticsAdapter(posthog, posthog.init()));
  }

  if (externalEnabled && env.VITE_SENTRY_DSN) {
    const sentry = new SentryDestination({
      dsn: env.VITE_SENTRY_DSN,
      release: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : undefined,
      environment: env.VITE_SENTRY_ENVIRONMENT,
    });
    destinations.push(new DeferredAnalyticsAdapter(sentry, sentry.init()));
  }

  if (env.VITE_COLLECTOR_URL) {
    destinations.push(
      new BackendCollectorDestination({
        collectorUrl: env.VITE_COLLECTOR_URL,
        platform: collectorPlatform(env.VITE_PLATFORM),
        appVersion: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : undefined,
      }),
    );
  }

  return new CompositeAnalyticsAdapter(destinations);
}
